import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const distances = [
  { label: 'München Hbf',     value: '25 min', icon: 'ri-train-line' },
  { label: 'Starnberger See', value: '10 min', icon: 'ri-water-flash-line' },
  { label: 'Flughafen MUC',   value: '45 min', icon: 'ri-plane-line' },
  { label: 'Zugspitze',       value: '90 min', icon: 'ri-landscape-line' },
];

const highlights = [
  { icon: 'ri-home-smile-line', title: 'Gauting im Würmtal',          description: 'Ruhige Lage im malerischen Würmtal – südwestlich von München, sowohl mit dem Auto als auch mit der S-Bahn ideal zu erreichen.' },
  { icon: 'ri-building-line',   title: 'Nach München',                description: 'In nur 25 Minuten ins Münchner Stadtzentrum – mit dem Auto oder der S-Bahn, die nur wenige Gehminuten entfernt liegt.' },
  { icon: 'ri-sailboat-line',   title: 'Zum Starnberger See',         description: 'Der Starnberger See liegt nur eine kurze Fahrt entfernt – ideal für Spaziergänge, Bootsfahrten und Ausflüge.' },
  { icon: 'ri-compass-3-line',  title: 'Zu anderen Seen und den Alpen', description: 'Nicht nur Seen wie der Ammersee, Wörthsee oder Staffelsee liegen nah, sondern auch die Berge. Die Zugspitze ist direkt mit S-Bahn und Zug erreichbar, mit dem Auto sowieso.' },
  { icon: 'ri-road-map-line',   title: 'Bestens erreichbar',          description: 'Kostenlose Parkplätze, Autobahnanbindung (A95/Garmisch, A96/Lindau) und die S-Bahn in Laufnähe.' },
];

const KICKER = 'Unsere Lage';
const TITLE_1 = 'Ruhig gelegen.';
const TITLE_2 = 'Klug verbunden.';
const LEAD = 'Gauting verbindet entspannte Ruhe mit vielen Möglichkeiten. Der Starnberger See, München und die schönsten Seiten der Region liegen angenehm nah.';

const PATCH = '/images/alpenhof_patch_mobile.png';

function VariantSeparator({ label }: { label: string }) {
  return (
    <div className="bg-stone-900 text-white py-4 px-6 text-center sticky top-14 lg:top-20 z-30">
      <p className="text-xs sm:text-sm font-bold tracking-widest uppercase">{label}</p>
    </div>
  );
}

/* ─── VARIANTE A — „Der Aufnäher" ─────────────────────────────────────── */
function VariantA() {
  return (
    <section className="bg-amber-50/50 py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #78350f 0 2px, transparent 2px 4px), repeating-linear-gradient(-45deg, #78350f 0 2px, transparent 2px 4px)",
        }}
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <img
          src={PATCH}
          alt="Lage Hotel Alpenhof Gauting"
          className="w-full max-w-md mx-auto mb-6 sm:mb-8"
          style={{
            transform: 'rotate(-2deg)',
            filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.18)) drop-shadow(0 4px 8px rgba(0,0,0,0.12))',
          }}
        />

        <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-10">
          {distances.map((d) => (
            <div key={d.label} className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 border border-amber-100 shadow-sm">
              <i className={`${d.icon} text-base text-amber-700`}></i>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 leading-none mb-1">{d.label}</p>
                <p className="text-sm font-bold text-stone-900 leading-none">{d.value}</p>
              </div>
            </div>
          ))}
        </div>

        <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-3 px-4 py-1.5 bg-stone-100 text-stone-700 rounded-full">
          {KICKER}
        </span>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-stone-900 mb-4 leading-tight">
          {TITLE_1}<br />
          <span className="text-stone-700">{TITLE_2}</span>
        </h2>
        <p className="text-stone-700 leading-relaxed mb-8 max-w-xl mx-auto">
          {LEAD}
        </p>

        <ul className="space-y-4 max-w-xl mx-auto text-left">
          {highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className={`${h.icon} text-lg text-amber-700 mt-0.5 flex-shrink-0`}></i>
              <div>
                <span className="text-sm font-semibold text-stone-900">{h.title}</span>
                <p className="text-sm text-stone-700 leading-relaxed">{h.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── VARIANTE B — „Magazin-Layout" ────────────────────────────────────── */
function VariantB() {
  return (
    <section className="bg-stone-50 py-16 sm:py-32 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto relative">
        <div className="relative sm:absolute sm:top-0 sm:left-0 sm:w-[55%] z-10">
          <img
            src={PATCH}
            alt="Lage Hotel Alpenhof Gauting"
            className="w-full"
            style={{
              transform: 'rotate(-3deg)',
              filter: 'drop-shadow(0 18px 32px rgba(0,0,0,0.20)) drop-shadow(0 6px 12px rgba(0,0,0,0.12))',
            }}
          />
        </div>

        <div className="relative sm:ml-[40%] sm:mt-[28%] -mt-12 z-20 bg-white rounded-3xl shadow-2xl border border-stone-100 p-6 sm:p-10">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-3 px-4 py-1.5 bg-amber-50 text-amber-700 rounded-full">
            {KICKER}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-stone-900 mb-4 leading-tight">
            {TITLE_1}<br />
            <span className="text-stone-600">{TITLE_2}</span>
          </h2>
          <p className="text-stone-700 leading-relaxed mb-6">
            {LEAD}
          </p>

          <ul className="space-y-3 mb-6">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3">
                <i className={`${h.icon} text-lg text-amber-700 mt-0.5 flex-shrink-0`}></i>
                <div>
                  <span className="text-sm font-semibold text-stone-900">{h.title}</span>
                  <p className="text-sm text-stone-700 leading-relaxed">{h.description}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-2 gap-3">
            {distances.map((d) => (
              <div key={d.label} className="flex items-center gap-3 bg-stone-50 rounded-xl px-3 py-2.5 border border-stone-100">
                <i className={`${d.icon} text-base text-amber-700`}></i>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 leading-none mb-0.5">{d.label}</p>
                  <p className="text-sm font-bold text-stone-900 leading-none">{d.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── VARIANTE C — „Echte Karte, Patch als Akzent" ────────────────────── */
function VariantC() {
  const mapEmbedUrl =
    'https://maps.google.com/maps?q=Hotel+Alpenhof+Gauting,+Unterbrunner+Stra%C3%9Fe+9,+82131+Gauting&z=11&output=embed';
  const directionsUrl =
    'https://www.google.com/maps/dir/?api=1&destination=Hotel+Alpenhof+Gauting,+Unterbrunner+Stra%C3%9Fe+9,+82131+Gauting';

  return (
    <section className="bg-white py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
          <img
            src={PATCH}
            alt="Lage Hotel Alpenhof Gauting"
            className="w-20 sm:w-28 h-auto flex-shrink-0"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
          />
          <div className="min-w-0">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-2 text-amber-700">
              {KICKER}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-stone-900 leading-tight">
              {TITLE_1}<br className="sm:hidden" /> {TITLE_2}
            </h2>
          </div>
        </div>

        <p className="text-stone-700 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
          {LEAD}
        </p>

        <div className="rounded-2xl overflow-hidden shadow-lg border border-stone-100 mb-6">
          <iframe
            src={mapEmbedUrl}
            className="w-full h-[400px] sm:h-[450px] block"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Karte Hotel Alpenhof Gauting"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {distances.map((d) => (
            <div key={d.label} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2.5 border border-stone-100">
              <i className={`${d.icon} text-base text-amber-700`}></i>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-stone-500 leading-none mb-0.5">{d.label}</p>
                <p className="text-sm font-bold text-stone-900 leading-none">{d.value}</p>
              </div>
            </div>
          ))}
        </div>

        <ul className="space-y-3 mb-8 max-w-3xl">
          {highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-3">
              <i className={`${h.icon} text-lg text-amber-700 mt-0.5 flex-shrink-0`}></i>
              <div>
                <span className="text-sm font-semibold text-stone-900">{h.title}</span>
                <p className="text-sm text-stone-700 leading-relaxed">{h.description}</p>
              </div>
            </li>
          ))}
        </ul>

        <a
          href={directionsUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-amber-600 transition-colors"
        >
          <i className="ri-route-line"></i>
          Route planen
          <i className="ri-external-link-line text-xs"></i>
        </a>
      </div>
    </section>
  );
}

export default function LagePreviewPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="bg-amber-700 text-white px-6 py-8 mt-14 lg:mt-20 text-center">
        <h1 className="text-2xl sm:text-3xl font-serif">Vorschau – Lage-Sektion</h1>
        <p className="text-amber-100 text-sm mt-2">Drei Varianten zum Vergleich – mit allen Texten der echten Sektion.</p>
      </div>

      <VariantSeparator label="Variante A — Der Aufnäher" />
      <VariantA />

      <VariantSeparator label="Variante B — Magazin-Layout" />
      <VariantB />

      <VariantSeparator label="Variante C — Echte Karte, Patch als Akzent" />
      <VariantC />

      <Footer />
    </div>
  );
}
